import { externalJobsService } from '@/services/external-jobs.service'
import type {
  ExternalJobMatchRequest,
  ExternalJobSearchParams,
  JobApplyResumeStatus,
  JobCompanyTab,
} from '@/types'
import {
  type Query,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'

const PAGE_SIZE = 20

// Query key factory for external jobs
export const externalJobsKeys = {
  all: ['external-jobs'] as const,
  lists: () => [...externalJobsKeys.all, 'list'] as const,
  list: (params: ExternalJobSearchParams) => [...externalJobsKeys.lists(), params] as const,
  details: () => [...externalJobsKeys.all, 'detail'] as const,
  detail: (id: number) => [...externalJobsKeys.details(), id] as const,
  connections: (id: number) => [...externalJobsKeys.all, 'connections', id] as const,
  recommended: () => [...externalJobsKeys.all, 'recommended'] as const,
  categories: () => [...externalJobsKeys.all, 'categories'] as const,
  matches: () => [...externalJobsKeys.all, 'matches'] as const,
  // Shared prefix so invalidating one apply-resume key refreshes both the
  // per-job queries and the batch query below.
  applyResumeAll: () => [...externalJobsKeys.all, 'apply-resume'] as const,
  applyResume: (jobId: number) => [...externalJobsKeys.applyResumeAll(), jobId] as const,
  applyResumeStatuses: (jobIds: number[]) =>
    [...externalJobsKeys.applyResumeAll(), 'batch', jobIds] as const,
}

export function useExternalJobs(params: ExternalJobSearchParams = {}) {
  return useQuery({
    queryKey: externalJobsKeys.list(params),
    queryFn: () => externalJobsService.searchJobs(params),
  })
}

export function useInfiniteExternalJobs(
  params: Omit<ExternalJobSearchParams, 'skip' | 'limit'> = {},
) {
  return useInfiniteQuery({
    queryKey: [...externalJobsKeys.lists(), 'infinite', params],
    queryFn: ({ pageParam = 0, signal }) =>
      externalJobsService.searchJobs(
        {
          ...params,
          skip: pageParam,
          limit: PAGE_SIZE,
        },
        signal,
      ),
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer items than requested, there are no more
      if (lastPage.items.length < PAGE_SIZE) {
        return undefined
      }
      // Return the next skip value
      return allPages.reduce((total, page) => total + page.items.length, 0)
    },
  })
}

export function useExternalJob(jobId: number) {
  return useQuery({
    queryKey: externalJobsKeys.detail(jobId),
    queryFn: () => externalJobsService.getJob(jobId),
    enabled: !!jobId,
  })
}

/** Job-detail Company tab (connection links + company overview). Separate
 * from useExternalJob on purpose -- resolving a brand-new company can take
 * a few seconds server-side, and must never block the rest of the job
 * detail view. Only ever mounted from inside the Company tab itself, so
 * this naturally never fires for jobs whose Company tab nobody opens.
 *
 * The first fetch runs the (fire-and-forget) resolution attempt; every
 * fetch after that is poll mode (`check: true`) so a refresh never kicks off
 * another LLM/Piloterr attempt. While the backend reports `resolving` it
 * auto-polls, matching useApplyResumeStatusesForJobs' interval pattern. */
export function useJobCompanyTab(jobId: number | null) {
  // Per-job, per-mount: which jobs have already had their one resolution
  // attempt. A Set (not a single boolean) so switching jobs in the same
  // mounted panel still gets each job its first non-check fetch.
  const resolutionKickedOff = useRef<Set<number>>(new Set())

  return useQuery({
    queryKey: externalJobsKeys.connections(jobId ?? 0),
    queryFn: () => {
      const id = jobId as number
      const isFirstFetch = !resolutionKickedOff.current.has(id)
      resolutionKickedOff.current.add(id)
      return externalJobsService.getJobCompanyTab(id, { check: !isFirstFetch })
    },
    enabled: !!jobId,
    refetchInterval: (query: Query<JobCompanyTab, Error>) =>
      query.state.data?.status === 'resolving' ? 3000 : false,
  })
}

export function useRecommendedJobs(limit = 10) {
  return useQuery({
    queryKey: externalJobsKeys.recommended(),
    queryFn: () => externalJobsService.getRecommendedJobs(limit),
  })
}

export function useJobCategories() {
  return useQuery({
    queryKey: externalJobsKeys.categories(),
    queryFn: () => externalJobsService.getCategories(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  })
}

export function useAnalyzeJobMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      request,
      chatId,
    }: {
      jobId: number
      request?: ExternalJobMatchRequest
      chatId?: string | null
    }) => externalJobsService.analyzeMatch(jobId, request, chatId),
    onSuccess: () => {
      // Invalidate matches and recommended jobs
      queryClient.invalidateQueries({ queryKey: externalJobsKeys.matches() })
      queryClient.invalidateQueries({ queryKey: externalJobsKeys.recommended() })
    },
  })
}

export function useMyJobMatches(skip = 0, limit = 20, enabled = true) {
  return useQuery({
    queryKey: [...externalJobsKeys.matches(), skip, limit],
    queryFn: () => externalJobsService.getMyMatches(skip, limit),
    enabled,
  })
}

/** Apply-resume runs that should poll until terminal state. */
const APPLY_RESUME_ACTIVE_STATUSES = new Set(['preparing', 'generating_revision', 'reanalyzing'])

export function useApplyResumeStatus(jobId: number | null) {
  return useQuery<JobApplyResumeStatus | null>({
    queryKey: externalJobsKeys.applyResume(jobId || 0),
    queryFn: () => externalJobsService.getApplyResumeStatus(jobId || 0),
    enabled: Boolean(jobId),
    retry: false,
  })
}

const APPLY_RESUME_STALE_MS = 5 * 60 * 1000

/**
 * Fetch apply-resume run status for many jobs (cards persist after refresh).
 * Polls every 2s while any status is preparing / generating_revision / reanalyzing.
 *
 * Issues a single batched request rather than one GET per job: the previous
 * per-card fan-out fired dozens of concurrent requests on page load and tripped
 * the edge rate limiter.
 */
export function useApplyResumeStatusesForJobs(jobIds: number[]) {
  // Sort so the query key is stable regardless of incoming order.
  const ids = [...new Set(jobIds.filter((id) => Number.isFinite(id) && id > 0))].sort(
    (a, b) => a - b,
  )

  const query = useQuery<JobApplyResumeStatus[]>({
    queryKey: externalJobsKeys.applyResumeStatuses(ids),
    queryFn: () => externalJobsService.getApplyResumeStatuses(ids),
    enabled: ids.length > 0,
    staleTime: APPLY_RESUME_STALE_MS,
    retry: false,
    refetchInterval: (q: Query<JobApplyResumeStatus[], Error>) => {
      const runs = q.state.data
      const anyActive = runs?.some((r) => APPLY_RESUME_ACTIVE_STATUSES.has(r.status)) ?? false
      return anyActive ? 2000 : false
    },
  })

  const runsByJobId: Record<number, JobApplyResumeStatus> = {}
  for (const run of query.data ?? []) {
    runsByJobId[run.job_listing_id] = run
  }

  return { runsByJobId, jobIds: ids }
}

export function useStartApplyResume() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: number) => externalJobsService.startApplyResume(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(externalJobsKeys.applyResume(data.job_listing_id), data)
      // Prefix invalidation refreshes both the per-job query and the batch query.
      void queryClient.invalidateQueries({ queryKey: externalJobsKeys.applyResumeAll() })
    },
  })
}

export function useRetryApplyResume() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: number) => externalJobsService.retryApplyResume(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(externalJobsKeys.applyResume(data.job_listing_id), data)
      // Prefix invalidation refreshes both the per-job query and the batch query.
      void queryClient.invalidateQueries({ queryKey: externalJobsKeys.applyResumeAll() })
    },
  })
}
